import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map, of } from 'rxjs';

import { EndClientReportService } from '../end-client-report.service';
import type { DeckLoad } from '../end-client-report.service';
import { fixtureDesign, fixtureRecords, fixtureSurveyLayout } from '../end-client-reporting.fixtures';
import type { RespondentRecord, SurveyLayout } from '../report-aggregation';
import type { EndClientReport, ReportDesign } from '../../../models/end-client-reporting-package';

/** What the theme editor previews: the bundled sample deck, or a real report or template. */
export type ThemePreviewSource =
  | { kind: 'sample' }
  | { kind: 'report'; id: string }
  | { kind: 'template'; id: string };

/**
 * NK-450 — the deck's data for the theme editor's preview.
 *
 * Stands in for `EndClientReportService` the same way `ShareLinkReportService` does for a share
 * link: provided in its place around the preview, so the deck component loads exactly as it always
 * does. Only the four reads the deck performs on load are answered, each from whichever source was
 * chosen; the preview is drawn in client view, so nothing ever asks it to save.
 */
@Injectable()
export class ThemePreviewReportService extends EndClientReportService {
  source: ThemePreviewSource = { kind: 'sample' };

  constructor(httpClient: HttpClient) {
    super(httpClient);
  }

  // Not through the browser cache: this isn't the logged-in user's report data.
  override loadDeck(reportId: string): Observable<DeckLoad> {
    return this.loadDeckUncached(reportId);
  }

  override getReport(): Observable<EndClientReport> {
    const source = this.source;
    switch (source.kind) {
      case 'report':
        return super.getReport(source.id);
      case 'template':
        // The deck reads a report row only for its name and description; a template has both.
        return this.getTemplate(source.id).pipe(map(template => ({
          id: '',
          projectId: '',
          name: template.name,
          description: template.description,
          reportDesignVersion: template.templateVersion,
        }) as EndClientReport));
      default:
        return of({
          id: '',
          projectId: '',
          name: 'Sample report',
          description: 'How this theme looks across a range of modules.',
          reportDesignVersion: 0,
        } as EndClientReport);
    }
  }

  override getReportDesign(): Observable<ReportDesign> {
    const source = this.source;
    switch (source.kind) {
      case 'report': return super.getReportDesign(source.id);
      case 'template': return this.getTemplateContent(source.id);
      default: return of(fixtureDesign());
    }
  }

  override getRecords(): Observable<RespondentRecord[]> {
    const source = this.source;
    switch (source.kind) {
      case 'report': return super.getRecords(source.id);
      case 'template': return this.getTemplateRecords(source.id);
      default: return of(fixtureRecords());
    }
  }

  override getSurveyLayout(): Observable<SurveyLayout> {
    const source = this.source;
    switch (source.kind) {
      case 'report': return super.getSurveyLayout(source.id);
      case 'template': return this.getTemplateSurveyLayout(source.id);
      default: return of(fixtureSurveyLayout());
    }
  }
}
